import { Button } from '@shared/components/ui/button';
import { Input } from '@shared/components/ui/input';
import { ModalCenter, type ModalCenterRef } from '@shared/components/ui/ModalCenter';
import { Combobox } from '@shared/components/ui/AdvancedFilter/components';
import { forwardRef, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useCountries, useUpdateOrder } from '../../hooks';
import type { OrderCustomer, OrderLine } from '../../models/Order';

interface ShippingDataModalProps {
  customer?: OrderCustomer;
  orderId?: number;
  lines?: OrderLine[];
  onUpdate?: () => void;
}

export const ShippingDataModal = forwardRef<ModalCenterRef, ShippingDataModalProps>(
  ({ customer, orderId, lines = [], onUpdate }, ref) => {
    const { t } = useTranslation('viewOrdersModule');
    const { countries, loading: countriesLoading } = useCountries();
    const { updateOrder, loading: isUpdating } = useUpdateOrder();

    const [formData, setFormData] = useState({
      name: '',
      email: '',
      phone: '',
      address1: '',
      address2: '',
      postalCode: '',
      city: '',
      province: '',
      country: '',
    });

    useEffect(() => {
      if (customer) {
        setFormData({
          name: customer.name || '',
          email: customer.email || '',
          phone: customer.phone || '',
          address1: customer.address.address1 || '',
          address2: customer.address.address2 || '',
          postalCode: customer.address.zip || '',
          city: customer.address.city || '',
          province: customer.address.province || '',
          country: customer.address.country_code || '',
        });
      }
    }, [customer]);

    const handleInputChange = (field: string, value: string) => {
      setFormData((prev) => ({
        ...prev,
        [field]: value,
      }));
    };

    const handleSave = async () => {
      if (!orderId) return;

      const linesPayload = lines.map((line) => {
        const linePayload: any = {
          product_id: line.product_id,
          quantity: line.qty, // API espera 'quantity', no 'qty'
          amount: line.amount,
        };

        // Solo agregar parámetros si tienen valores
        if (line.external_product_id) {
          linePayload.external_product_id = line.external_product_id;
        }
        if (line.external_variant_id) {
          linePayload.external_variant_id = line.external_variant_id;
        }
        if (line.pack_id) {
          linePayload.pack_id = line.pack_id;
        }

        return linePayload;
      });

      const payload = {
        customer: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          address: {
            address_1: formData.address1,
            address_2: formData.address2,
            zip: formData.postalCode,
            city: formData.city,
            province: formData.province,
            country_code: formData.country,
          },
        },
        lines: linesPayload,
      };

      const success = await updateOrder(orderId, payload);

      if (success) {
        onUpdate?.();
        handleClose();
      }
    };

    const handleClose = () => {
      if (ref && 'current' in ref && ref.current) {
        ref.current.closeModal();
      }
    };

    const countryOptions = useMemo(() => {
      return countries.map((country) => ({
        label: country.name,
        value: country.code,
      }));
    }, [countries]);

    return (
      <ModalCenter ref={ref} title={t('modals.shipping-data.title')} width="lg" type="default" fullScreenMobile={true}>
        <div className="flex flex-col h-full">
          {/* Contenido principal */}
          <div className="flex-1 flex flex-col gap-6 py-4 text-sm max-h-[83vh] sm:max-h-[75vh] overflow-y-auto pb-24 md:pb-4">
            <div>
              <h3 className="text-base font-semibold text-title">{t('modals.shipping-data.customer')}</h3>
              <div className="mt-4 flex flex-col gap-4">
                <div>
                  <label htmlFor="name" className="font-medium">
                    {t('modals.shipping-data.name')} <span className="text-error">*</span>
                  </label>
                  <Input
                    id="name"
                    placeholder={t('modals.shipping-data.placeholders.name')}
                    className="mt-1"
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="email" className="font-medium">
                      {t('modals.shipping-data.email')}
                    </label>
                    <Input
                      id="email"
                      placeholder={t('modals.shipping-data.placeholders.email')}
                      type="email"
                      className="mt-1"
                      value={formData.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="font-medium">
                      {t('modals.shipping-data.phone')} <span className="text-error">*</span>
                    </label>
                    <Input
                      id="phone"
                      placeholder={t('modals.shipping-data.placeholders.phone')}
                      type="tel"
                      className="mt-1"
                      value={formData.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-base font-semibold text-title">{t('modals.shipping-data.address')}</h3>
              <div className="mt-4 flex flex-col gap-4">
                <div>
                  <label htmlFor="address1" className="font-medium">
                    {t('modals.shipping-data.address1')} <span className="text-error">*</span>
                  </label>
                  <Input
                    id="address1"
                    placeholder={t('modals.shipping-data.placeholders.address1')}
                    className="mt-1"
                    value={formData.address1}
                    onChange={(e) => handleInputChange('address1', e.target.value)}
                  />
                </div>
                <div>
                  <label htmlFor="address2" className="font-medium">
                    {t('modals.shipping-data.address2')}
                  </label>
                  <Input
                    id="address2"
                    placeholder={t('modals.shipping-data.placeholders.address2')}
                    className="mt-1"
                    value={formData.address2}
                    onChange={(e) => handleInputChange('address2', e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="postalCode" className="font-medium">
                      {t('modals.shipping-data.postal-code')} <span className="text-error">*</span>
                    </label>
                    <Input
                      id="postalCode"
                      placeholder={t('modals.shipping-data.placeholders.postal-code')}
                      className="mt-1"
                      value={formData.postalCode}
                      onChange={(e) => handleInputChange('postalCode', e.target.value)}
                    />
                  </div>
                  <div>
                    <label htmlFor="city" className="font-medium">
                      {t('modals.shipping-data.city')} <span className="text-error">*</span>
                    </label>
                    <Input
                      id="city"
                      placeholder={t('modals.shipping-data.placeholders.city')}
                      className="mt-1"
                      value={formData.city}
                      onChange={(e) => handleInputChange('city', e.target.value)}
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div>
                    <label htmlFor="province" className="font-medium">
                      {t('modals.shipping-data.province')}
                    </label>
                    <Input
                      id="province"
                      placeholder={t('modals.shipping-data.placeholders.province')}
                      className="mt-1"
                      value={formData.province}
                      onChange={(e) => handleInputChange('province', e.target.value)}
                    />
                  </div>
                  <div>
                    <label className="font-medium">{t('modals.shipping-data.country')}</label>
                    <Combobox
                      options={countryOptions}
                      value={formData.country}
                      setValue={(value: string) => handleInputChange('country', value)}
                      placeholder={t('modals.shipping-data.placeholders.country')}
                      disabled={countriesLoading}
                      className="w-full mt-1"
                      dataTestId="shipping-country-combobox"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Botones de acción - Fijos en mobile */}
          <div className="fixed bottom-0 left-0 right-0 p-4 md:pb-0 md:px-0 md:relative md:border-t-0">
            <div className="flex justify-end gap-2">
              <Button variant="outline" size="lg" onClick={handleClose}>
                {t('modals.shipping-data.cancel')}
              </Button>
              <Button variant="primary" size="lg" onClick={handleSave} disabled={isUpdating}>
                {isUpdating ? t('modals.shipping-data.saving') : t('modals.shipping-data.save')}
              </Button>
            </div>
          </div>
        </div>
      </ModalCenter>
    );
  },
);

ShippingDataModal.displayName = 'ShippingDataModal';
